import { GetStaticPathsResult, NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import { useRouter } from 'next/router'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import createApolloClient from 'components/withApollo/createApolloClient'
import { useLotQuery } from 'components/pages/LotBasePage/types/lotQuery'
import PageLayout from 'components/pages/PageLayout'
import React, { useEffect } from 'react'
import LoadingPage from 'components/pages/LoadingPage'
import NotFoundPage from 'components/pages/NotFoundPage'
import PageMeta from 'components/PageMeta'
import ErrorPage from 'components/pages/ErrorPage'
import {
  LotPaths,
  LotPathsDocument,
  LotPathsVariables
} from 'components/pages/LotBasePage/types/lotPathsQuery'
import LotGroupPage from 'components/pages/LotGroupPage'
import LotPage from 'components/pages/LotPage'
import useReferrals from 'hooks/useReferrals'
import getMutationError from 'utils/getMutationError'
import useCart from 'queries/useCart'
import pathFromUrl from 'utils/pathFromUrl'

import { useCartUpdateLotNotes } from './types/CartUpdateLotNotes'

type Params = {
  seoName: string
  notes?: string
  asPath: string
}

type Props = {
  refetch: () => void
}

function LotBasePage() {
  const router = useRouter()
  const { seoName, notes } = router.query as Params

  const { cart } = useCart({ skip: typeof window === 'undefined' })

  const { data, loading, error } = useLotQuery({
    variables: {
      seoName: seoName.toLowerCase()
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  })

  const [cartUpdateLotNotes] = useCartUpdateLotNotes({})
  useEffect(() => {
    if (!notes || !notes.length || !cart?.uuid) return

    cartUpdateLotNotes({
      variables: {
        cartUuid: cart.uuid,
        notes
      }
    }).then((response) => {
      const responseError = getMutationError(response!)
      if (responseError) {
        throw responseError
      }
    })
  }, [notes, cart?.uuid, cartUpdateLotNotes])

  useReferrals()

  const lot = data?.lot
  const lotGroup = data?.lotGroup
  const globalConfig = data?.globalConfig

  if (loading && !lot && !lotGroup) return <LoadingPage />
  const notFoundError =
    error &&
    error.message.includes(
      'Cannot return null for non-nullable field Query.lot'
    )
  if (error && !notFoundError) return <ErrorPage message={error.message} />
  if (((!lot || !lot.active) && !lotGroup) || notFoundError) {
    return <NotFoundPage search={seoName} />
  }

  const hideMarketing =
    router.asPath.includes('boeing') || router.asPath.includes('homedepot')

  return (
    <PageLayout
      hideNavigation={hideMarketing}
      transparentOnTop={hideMarketing}
      fullWidth
      highlightCartItems
      staticPage
    >
      <>
        {lot && !lotGroup && (
          <>
            <PageMeta
              canonicalPath={pathFromUrl(lot.lotUrl)}
              coordinates={[lot.latitude, lot.longitude]}
              description={`Order online or view schedule from food trucks at ${lot.fullAddress}`}
              image={lot.foodImage ? lot.foodImage.medium : lot.image?.medium}
              ldJson={data?.structuredData?.lotPage}
              title={`Best Food Trucks | ${lot.name}`}
              url={lot?.lotUrl}
            />
            <LotPage globalConfig={globalConfig!} lot={lot} />
          </>
        )}
        {lotGroup && (
          <>
            <PageMeta
              canonicalPath={pathFromUrl(lotGroup.lotGroupUrl)}
              coordinates={[
                lotGroup?.lots?.[0]?.latitude ?? 0,
                lotGroup?.lots?.[0]?.longitude ?? 0
              ]}
              description={`Order online or view schedule from food trucks at ${lotGroup?.fullAddress}`}
              image={lotGroup.image?.medium}
              ldJson={data?.structuredData?.lotPage}
              title={`Best Food Trucks | ${lotGroup.name}`}
              url={lotGroup.lotGroupUrl}
            />
            <LotGroupPage globalConfig={globalConfig!} lotGroup={lotGroup} />
          </>
        )}
      </>
    </PageLayout>
  )
}

export default withApollo({ ssr: false })(LotBasePage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<LotPaths, LotPathsVariables>({
    query: LotPathsDocument
  })

  const paths = data.customerPaths.lotPaths.map((lotPath: string) => ({
    params: { seoName: lotPath }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(LotBasePage, {
  revalidate: 60 * 30 // 30 minutes
})
